import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  Stack,
  Tooltip,
  Collapse,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  Build as BuildIcon,
} from '@mui/icons-material';

// Field definitions based on the Synthetics API reference
interface FieldDefinition {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select';
  category: string;
  options?: string[];
}

const PANELIST_FIELDS: FieldDefinition[] = [
  // Demographics
  { name: 'age', label: 'Age', type: 'number', category: 'Demographics' },
  { name: 'gender', label: 'Gender', type: 'select', category: 'Demographics', options: ['Male', 'Female', 'Non-binary', 'Other'] },
  { name: 'location', label: 'Location', type: 'text', category: 'Demographics' },
  { name: 'occupation', label: 'Occupation', type: 'text', category: 'Demographics' },
  { name: 'zipcode', label: 'Zip Code', type: 'text', category: 'Demographics' },
  { name: 'city', label: 'City', type: 'text', category: 'Demographics' },
  { name: 'state', label: 'State', type: 'text', category: 'Demographics' },
  { name: 'ethnicity', label: 'Ethnicity', type: 'text', category: 'Demographics' },
  { name: 'marital_status', label: 'Marital Status', type: 'select', category: 'Demographics', options: ['Single', 'Married', 'Divorced', 'Widowed', 'Separated'] },
  { name: 'household_income', label: 'Household Income', type: 'number', category: 'Demographics' },
  { name: 'household_size', label: 'Household Size', type: 'number', category: 'Demographics' },
  
  // Persona Attributes
  { name: 'persona_generation', label: 'Generation', type: 'select', category: 'Persona', options: ['Gen Z', 'Millennial', 'Gen X', 'Baby Boomer', 'Silent Generation'] },
  { name: 'persona_life_stage', label: 'Life Stage', type: 'select', category: 'Persona', options: ['Young Adult', 'Early Career', 'Mid Career', 'Empty Nest', 'Retired'] },
  { name: 'persona_income_band', label: 'Income Band', type: 'select', category: 'Persona', options: ['Low', 'Lower-Middle', 'Middle', 'Upper-Middle', 'High'] },
  { name: 'persona_education', label: 'Education', type: 'select', category: 'Persona', options: ['High School', 'Some College', 'Associate', 'Bachelor', 'Master', 'Doctorate'] },
  { name: 'persona_household_type', label: 'Household Type', type: 'select', category: 'Persona', options: ['Single', 'Couple', 'Family with Children', 'Multi-Generational', 'Roommates'] },
  { name: 'persona_geography', label: 'Geography', type: 'select', category: 'Persona', options: ['Urban', 'Suburban', 'Rural'] },
  
  // Behavioral Traits
  { name: 'persona_behavior_money', label: 'Money Behavior', type: 'text', category: 'Behavioral' },
  { name: 'persona_behavior_media', label: 'Media Behavior', type: 'text', category: 'Behavioral' },
  { name: 'persona_behavior_work_learning', label: 'Work/Learning Behavior', type: 'text', category: 'Behavioral' },
  { name: 'persona_behavior_health_wellness', label: 'Health/Wellness Behavior', type: 'text', category: 'Behavioral' },
  
  // Additional
  { name: 'segment_name', label: 'Segment Name', type: 'text', category: 'Additional' },
  { name: 'interests', label: 'Interests', type: 'text', category: 'Additional' },
  { name: 'background', label: 'Background', type: 'text', category: 'Additional' },
];

// Operators based on field type
type Operator = 'equals' | 'contains' | 'gte' | 'lte' | 'range' | 'exists';

interface OperatorDefinition {
  value: Operator;
  label: string;
  applicableTo: ('text' | 'number' | 'select')[];
}

const OPERATORS: OperatorDefinition[] = [
  { value: 'equals', label: 'equals', applicableTo: ['text', 'number', 'select'] },
  { value: 'contains', label: 'contains', applicableTo: ['text'] },
  { value: 'gte', label: '≥ (greater or equal)', applicableTo: ['number'] },
  { value: 'lte', label: '≤ (less or equal)', applicableTo: ['number'] },
  { value: 'range', label: 'between', applicableTo: ['number'] },
  { value: 'exists', label: 'exists', applicableTo: ['text', 'number', 'select'] },
];

// Filter rule interface
interface FilterRule {
  id: string;
  field: string;
  operator: Operator;
  value: string;
  value2?: string; // For range operator
}

type LogicOperator = 'AND' | 'OR';

interface PanelistFilterBuilderProps {
  value: string; // JSON string
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const PanelistFilterBuilder: React.FC<PanelistFilterBuilderProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [rules, setRules] = useState<FilterRule[]>([]);
  const [logic, setLogic] = useState<LogicOperator>('AND');
  const [showRawJson, setShowRawJson] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  // Generate unique ID for rules
  const generateId = () => Math.random().toString(36).substring(2, 9);

  // Parse JSON value to rules on mount or when value changes externally
  useEffect(() => {
    if (!value || value === '{}') {
      setRules([]);
      setParseError(null);
      return;
    }

    try {
      const parsed = JSON.parse(value);
      
      // Try to parse OpenSearch DSL back to rules
      const parsedRules = parseOpenSearchDSL(parsed);
      if (parsedRules) {
        setRules(parsedRules.rules);
        setLogic(parsedRules.logic);
        setParseError(null);
      } else {
        // Complex query that can't be represented in the builder
        setShowRawJson(true);
        setParseError('Complex query detected. Showing raw JSON editor.');
      }
    } catch (e) {
      setParseError('Invalid JSON format');
    }
  }, []); // Only on mount

  // Parse OpenSearch DSL back to simple rules
  const parseOpenSearchDSL = (dsl: any): { rules: FilterRule[]; logic: LogicOperator } | null => {
    if (!dsl || typeof dsl !== 'object') return null;

    // Handle simple filter format: {"age_range": {"min": 25, "max": 45}, "gender": "female"}
    if (!dsl.query) {
      const rules: FilterRule[] = [];
      for (const [key, val] of Object.entries(dsl)) {
        if (key === 'age_range' && typeof val === 'object' && val !== null) {
          const ageRange = val as { min?: number; max?: number };
          if (ageRange.min !== undefined && ageRange.max !== undefined) {
            rules.push({
              id: generateId(),
              field: 'age',
              operator: 'range',
              value: String(ageRange.min),
              value2: String(ageRange.max),
            });
          } else if (ageRange.min !== undefined) {
            rules.push({
              id: generateId(),
              field: 'age',
              operator: 'gte',
              value: String(ageRange.min),
            });
          } else if (ageRange.max !== undefined) {
            rules.push({
              id: generateId(),
              field: 'age',
              operator: 'lte',
              value: String(ageRange.max),
            });
          }
        } else if (typeof val === 'string' || typeof val === 'number') {
          rules.push({
            id: generateId(),
            field: key,
            operator: 'equals',
            value: String(val),
          });
        }
      }
      return rules.length > 0 ? { rules, logic: 'AND' } : null;
    }

    // Handle OpenSearch DSL format
    const query = dsl.query;
    if (!query.bool) return null;

    const clauseType = query.bool.must ? 'must' : query.bool.should ? 'should' : null;
    if (!clauseType) return null;

    const clauses = query.bool[clauseType];
    if (!Array.isArray(clauses)) return null;

    const rules: FilterRule[] = [];
    for (const clause of clauses) {
      if (clause.match) {
        const [field, value] = Object.entries(clause.match)[0];
        rules.push({
          id: generateId(),
          field,
          operator: 'contains',
          value: String(value),
        });
      } else if (clause.term) {
        const [field, value] = Object.entries(clause.term)[0];
        rules.push({
          id: generateId(),
          field,
          operator: 'equals',
          value: String(value),
        });
      } else if (clause.range) {
        const [field, rangeObj] = Object.entries(clause.range)[0];
        const range = rangeObj as { gte?: number; lte?: number };
        if (range.gte !== undefined && range.lte !== undefined) {
          rules.push({
            id: generateId(),
            field,
            operator: 'range',
            value: String(range.gte),
            value2: String(range.lte),
          });
        } else if (range.gte !== undefined) {
          rules.push({
            id: generateId(),
            field,
            operator: 'gte',
            value: String(range.gte),
          });
        } else if (range.lte !== undefined) {
          rules.push({
            id: generateId(),
            field,
            operator: 'lte',
            value: String(range.lte),
          });
        }
      } else if (clause.exists) {
        rules.push({
          id: generateId(),
          field: clause.exists.field,
          operator: 'exists',
          value: '',
        });
      }
    }

    return rules.length > 0 ? { rules, logic: clauseType === 'must' ? 'AND' : 'OR' } : null;
  };

  // Convert rules to OpenSearch DSL JSON
  const rulesToOpenSearchDSL = useCallback((rules: FilterRule[], logic: LogicOperator): string => {
    if (rules.length === 0) {
      return '{}';
    }

    const clauses = rules.map(rule => {
      const fieldDef = PANELIST_FIELDS.find(f => f.name === rule.field);
      const isNumeric = fieldDef?.type === 'number';

      switch (rule.operator) {
        case 'equals':
          if (isNumeric) {
            return { term: { [rule.field]: Number(rule.value) } };
          }
          return { match: { [rule.field]: rule.value } };
        
        case 'contains':
          return { match: { [rule.field]: rule.value } };
        
        case 'gte':
          return { range: { [rule.field]: { gte: Number(rule.value) } } };
        
        case 'lte':
          return { range: { [rule.field]: { lte: Number(rule.value) } } };
        
        case 'range':
          return { range: { [rule.field]: { gte: Number(rule.value), lte: Number(rule.value2) } } };
        
        case 'exists':
          return { exists: { field: rule.field } };
        
        default:
          return { match: { [rule.field]: rule.value } };
      }
    });

    const boolClause = logic === 'AND' ? 'must' : 'should';
    const dsl = {
      query: {
        bool: {
          [boolClause]: clauses,
        },
      },
    };

    return JSON.stringify(dsl, null, 2);
  }, []);

  // Update parent when rules change
  const updateParentValue = useCallback((newRules: FilterRule[], newLogic: LogicOperator) => {
    const json = rulesToOpenSearchDSL(newRules, newLogic);
    onChange(json);
  }, [onChange, rulesToOpenSearchDSL]);

  // Add new rule
  const addRule = () => {
    const newRule: FilterRule = {
      id: generateId(),
      field: 'age',
      operator: 'gte',
      value: '',
    };
    const newRules = [...rules, newRule];
    setRules(newRules);
    updateParentValue(newRules, logic);
  };

  // Remove rule
  const removeRule = (id: string) => {
    const newRules = rules.filter(r => r.id !== id);
    setRules(newRules);
    updateParentValue(newRules, logic);
  };

  // Update rule
  const updateRule = (id: string, updates: Partial<FilterRule>) => {
    const newRules = rules.map(r => {
      if (r.id !== id) return r;
      
      const updated = { ...r, ...updates };
      
      // Reset value when field or operator changes
      if (updates.field !== undefined || updates.operator !== undefined) {
        const fieldDef = PANELIST_FIELDS.find(f => f.name === updated.field);
        const newOperator = updates.operator || r.operator;
        
        // If changing to exists operator, clear value
        if (newOperator === 'exists') {
          updated.value = '';
          updated.value2 = undefined;
        }
        
        // If field type doesn't support current operator, reset operator
        if (updates.field !== undefined && fieldDef) {
          const validOps = OPERATORS.filter(op => op.applicableTo.includes(fieldDef.type));
          if (!validOps.find(op => op.value === updated.operator)) {
            updated.operator = validOps[0]?.value || 'equals';
          }
        }
      }
      
      return updated;
    });
    setRules(newRules);
    updateParentValue(newRules, logic);
  };

  // Handle logic operator change
  const handleLogicChange = (_event: React.MouseEvent<HTMLElement>, newLogic: LogicOperator | null) => {
    if (newLogic) {
      setLogic(newLogic);
      updateParentValue(rules, newLogic);
    }
  };

  // Get available operators for a field
  const getOperatorsForField = (fieldName: string): OperatorDefinition[] => {
    const fieldDef = PANELIST_FIELDS.find(f => f.name === fieldName);
    if (!fieldDef) return OPERATORS;
    return OPERATORS.filter(op => op.applicableTo.includes(fieldDef.type));
  };

  // Get field definition
  const getFieldDef = (fieldName: string): FieldDefinition | undefined => {
    return PANELIST_FIELDS.find(f => f.name === fieldName);
  };

  // Group fields by category for the dropdown
  const fieldsByCategory = PANELIST_FIELDS.reduce((acc, field) => {
    if (!acc[field.category]) acc[field.category] = [];
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, FieldDefinition[]>);

  // Handle raw JSON change
  const handleRawJsonChange = (newValue: string) => {
    onChange(newValue);
    
    // Try to parse and update rules
    try {
      const parsed = JSON.parse(newValue);
      const result = parseOpenSearchDSL(parsed);
      if (result) {
        setRules(result.rules);
        setLogic(result.logic);
        setParseError(null);
      }
    } catch (e) {
      // Invalid JSON, that's ok during typing
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={500}>
          Panelist Filters
        </Typography>
        <Tooltip title={showRawJson ? 'Use Filter Builder' : 'Edit Raw JSON'}>
          <IconButton
            size="small"
            onClick={() => setShowRawJson(!showRawJson)}
            disabled={disabled}
          >
            {showRawJson ? <BuildIcon /> : <CodeIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {parseError && !showRawJson && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setParseError(null)}>
          {parseError}
        </Alert>
      )}

      <Collapse in={showRawJson}>
        <TextField
          fullWidth
          multiline
          rows={6}
          value={value}
          onChange={(e) => handleRawJsonChange(e.target.value)}
          disabled={disabled}
          placeholder='{"query": {"bool": {"must": [...]}}}'
          sx={{ fontFamily: 'monospace', mb: 2 }}
          helperText="Enter OpenSearch DSL query JSON"
        />
      </Collapse>

      <Collapse in={!showRawJson}>
        <Stack spacing={2}>
          {rules.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Match:
              </Typography>
              <ToggleButtonGroup
                value={logic}
                exclusive
                onChange={handleLogicChange}
                size="small"
                disabled={disabled}
              >
                <ToggleButton value="AND">
                  <Tooltip title="All conditions must match">
                    <span>ALL (AND)</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="OR">
                  <Tooltip title="Any condition can match">
                    <span>ANY (OR)</span>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {rules.map((rule, index) => {
            const fieldDef = getFieldDef(rule.field);
            const availableOps = getOperatorsForField(rule.field);

            return (
              <Paper
                key={rule.id}
                variant="outlined"
                sx={{ p: 1.5, backgroundColor: 'background.default' }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Field selector */}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Field</InputLabel>
                    <Select
                      value={rule.field}
                      label="Field"
                      onChange={(e) => updateRule(rule.id, { field: e.target.value })}
                      disabled={disabled}
                    >
                      {Object.entries(fieldsByCategory).map(([category, fields]) => [
                        <MenuItem key={`cat-${category}`} disabled sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                          {category}
                        </MenuItem>,
                        ...fields.map(field => (
                          <MenuItem key={field.name} value={field.name} sx={{ pl: 3 }}>
                            {field.label}
                          </MenuItem>
                        ))
                      ])}
                    </Select>
                  </FormControl>

                  {/* Operator selector */}
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={rule.operator}
                      label="Operator"
                      onChange={(e) => updateRule(rule.id, { operator: e.target.value as Operator })}
                      disabled={disabled}
                    >
                      {availableOps.map(op => (
                        <MenuItem key={op.value} value={op.value}>
                          {op.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Value input */}
                  {rule.operator !== 'exists' && (
                    <>
                      {fieldDef?.type === 'select' && fieldDef.options ? (
                        <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                          <InputLabel>Value</InputLabel>
                          <Select
                            value={rule.value}
                            label="Value"
                            onChange={(e) => updateRule(rule.id, { value: e.target.value })}
                            disabled={disabled}
                          >
                            {fieldDef.options.map(opt => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          size="small"
                          label={rule.operator === 'range' ? 'Min' : 'Value'}
                          type={fieldDef?.type === 'number' ? 'number' : 'text'}
                          value={rule.value}
                          onChange={(e) => updateRule(rule.id, { value: e.target.value })}
                          disabled={disabled}
                          sx={{ flex: 1, minWidth: 100 }}
                        />
                      )}

                      {/* Second value for range */}
                      {rule.operator === 'range' && (
                        <TextField
                          size="small"
                          label="Max"
                          type="number"
                          value={rule.value2 || ''}
                          onChange={(e) => updateRule(rule.id, { value2: e.target.value })}
                          disabled={disabled}
                          sx={{ flex: 1, minWidth: 100 }}
                        />
                      )}
                    </>
                  )}

                  {/* Delete button */}
                  <IconButton
                    size="small"
                    onClick={() => removeRule(rule.id)}
                    disabled={disabled}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Show logic connector between rules */}
                {index < rules.length - 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Chip
                      label={logic}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>
                )}
              </Paper>
            );
          })}

          <Button
            startIcon={<AddIcon />}
            onClick={addRule}
            disabled={disabled}
            variant="outlined"
            size="small"
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Filter
          </Button>

          {rules.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No filters applied. Click "Add Filter" to narrow down panelist selection.
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
};

export default PanelistFilterBuilder;



